import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo, servicesList, serviceAreas } from '../data/mock';
import { serviceImages, serviceDescriptions, serviceFeatures } from '../data/serviceData';
import { pageSEO } from '../data/seoData';
import SEOHead from '../components/SEOHead';

const ServicesPage = () => {
  return (
    <div>
      <SEOHead 
        title={pageSEO.services.title}
        description={pageSEO.services.description}
        canonical="/services"
      />
      {/* Hero Section */}
      <section className="relative py-24 bg-neutral-900">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url('https://images.pexels.com/photos/2219024/pexels-photo-2219024.jpeg?auto=compress&cs=tinysrgb&w=1260')` }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Our Residential Services</h1>
          <p className="text-xl text-neutral-300 max-w-3xl mx-auto">
            Comprehensive contracting services across Dallas-Fort Worth with a focus on painting, remodeling, and quality craftsmanship.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid gap-16">
            {servicesList.map((service, index) => (
              <div key={service.id} className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="relative overflow-hidden rounded-2xl shadow-xl group">
                    <img 
                      src={serviceImages[service.id]?.image} 
                      alt={service.title}
                      className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/60 to-transparent" />
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">{service.shortTitle}</span>
                  <h2 className="text-3xl font-bold text-neutral-900 mt-2 mb-4">{service.title}</h2>
                  <p className="text-neutral-600 mb-6">{serviceDescriptions[service.id]}</p>
                  
                  <div className="grid sm:grid-cols-2 gap-3 mb-8">
                    {serviceFeatures[service.id]?.slice(0, 6).map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                        <span className="text-sm text-neutral-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Link to={`/services/${service.id}`}>
                    <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                      Learn More About {service.shortTitle}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Coverage Area</span>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-4 mb-4">Serving Dallas-Fort Worth</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {serviceAreas.map((area, i) => {
              const slug = area.toLowerCase().replace(/\s+/g, '-');
              return (
                <Link key={i} to={`/services/residential-painting/${slug}`} className="bg-white p-4 rounded-lg shadow-sm text-center hover:shadow-md hover:border-amber-300 border border-transparent transition-all">
                  <span className="font-medium text-neutral-800">{area}</span>
                  <span className="text-neutral-500 text-sm">, TX</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Start Your Project?</h2>
          <p className="text-white/90 text-lg mb-8">Get a free estimate for any of our residential services.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-8 py-6 text-lg">
                Request Free Estimate <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-amber-600 font-semibold px-8 py-6 text-lg">
                <Phone className="mr-2 w-5 h-5" />{companyInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
